import React, { useState, useEffect } from 'react';
import { getAdminProductTypes, getAdminSizes, addPrintSize, updatePrintSize, deletePrintSize } from '../../api/api';

const AdminSizeManager = ({ onClose }) => {
  const [productTypes, setProductTypes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newSize, setNewSize] = useState({ label: '', productTypeId: '', displayOrder: 0 });
  const [editSize, setEditSize] = useState({ label: '', productTypeId: '', displayOrder: 0, isActive: true });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesResponse, sizesResponse] = await Promise.all([
        getAdminProductTypes(),
        getAdminSizes()
      ]);
      setProductTypes(typesResponse.productTypes || []);
      setSizes(sizesResponse.sizes || []);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSize = async (e) => {
    e.preventDefault();
    if (!newSize.label.trim()) {
      alert('Please enter a label');
      return;
    }
    if (!newSize.productTypeId) {
      alert('Please select a product type');
      return;
    }

    try {
      await addPrintSize(newSize.label, newSize.productTypeId, newSize.displayOrder);
      setNewSize({ label: '', productTypeId: '', displayOrder: 0 });
      loadData();
      alert('Print size added successfully!');
    } catch (error) {
      console.error('Error adding size:', error);
      alert(error.response?.data?.error || 'Failed to add print size');
    }
  };

  const handleStartEdit = (size) => {
    setEditingId(size.id);
    setEditSize({
      label: size.label,
      productTypeId: size.product_type_id,
      displayOrder: size.display_order,
      isActive: size.is_active
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditSize({ label: '', productTypeId: '', displayOrder: 0, isActive: true });
  };

  const handleSaveEdit = async (id) => {
    if (!editSize.label.trim()) {
      alert('Please enter a label');
      return;
    }
    if (!editSize.productTypeId) {
      alert('Please select a product type');
      return;
    }

    try {
      await updatePrintSize(id, editSize.label, editSize.productTypeId, editSize.displayOrder, editSize.isActive);
      setEditingId(null);
      loadData();
      alert('Print size updated successfully!');
    } catch (error) {
      console.error('Error updating size:', error);
      alert('Failed to update print size');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this print size?')) {
      return;
    }

    try {
      await deletePrintSize(id);
      loadData();
      alert('Print size deleted successfully!');
    } catch (error) {
      console.error('Error deleting size:', error);
      alert(error.response?.data?.error || 'Failed to delete print size');
    }
  };

  // Group sizes by product type
  const groupedSizes = productTypes.map(pt => ({
    ...pt,
    sizes: sizes.filter(s => s.product_type_id === pt.id)
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">📏 Manage Print Sizes</h2>
              <p className="text-sm text-gray-600 mt-1">Organized by product type</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Size Form */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Print Size</h3>
            <form onSubmit={handleAddSize} className="space-y-3">
              <div className="flex flex-wrap gap-3">
                <select
                  value={newSize.productTypeId}
                  onChange={(e) => setNewSize({ ...newSize, productTypeId: e.target.value })}
                  className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select Product Type</option>
                  {productTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>
                      {pt.name} {pt.api_param && `(${pt.api_param})`}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newSize.label}
                  onChange={(e) => setNewSize({ ...newSize, label: e.target.value })}
                  placeholder="Size label (e.g., 4R Size, A4 Metal)"
                  className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
                <input
                  type="number"
                  value={newSize.displayOrder}
                  onChange={(e) => setNewSize({ ...newSize, displayOrder: parseInt(e.target.value) })}
                  placeholder="Order"
                  className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                >
                  ➕ Add
                </button>
              </div>
            </form>
          </div>

          {/* Sizes List Grouped by Product Type */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Existing Print Sizes</h3>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : groupedSizes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No product types found
              </div>
            ) : (
              <div className="space-y-6">
                {groupedSizes.map((group) => (
                  <div key={group.id} className="border border-gray-200 rounded-lg p-4">
                    {/* Product Type Header */}
                    <div className="mb-4 pb-3 border-b border-gray-200">
                      <h4 className="text-lg font-bold text-primary-600">
                        {group.name}
                        {group.api_param && (
                          <span className="ml-2 text-sm text-gray-500 font-normal">
                            API: ?{group.api_param}
                          </span>
                        )}
                      </h4>
                      {group.description && (
                        <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                      )}
                    </div>

                    {/* Sizes for this Product Type */}
                    {group.sizes.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">No sizes for this product type yet</p>
                    ) : (
                      <div className="space-y-3">
                        {group.sizes.map((size) => (
                          <div
                            key={size.id}
                            className="border border-gray-200 rounded-lg p-3 flex items-center justify-between bg-white"
                          >
                            {editingId === size.id ? (
                              <div className="flex-1 flex flex-wrap items-center gap-3">
                                <select
                                  value={editSize.productTypeId}
                                  onChange={(e) => setEditSize({ ...editSize, productTypeId: e.target.value })}
                                  className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                >
                                  {productTypes.map(pt => (
                                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  value={editSize.label}
                                  onChange={(e) => setEditSize({ ...editSize, label: e.target.value })}
                                  className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                                <input
                                  type="number"
                                  value={editSize.displayOrder}
                                  onChange={(e) => setEditSize({ ...editSize, displayOrder: parseInt(e.target.value) })}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={editSize.isActive}
                                    onChange={(e) => setEditSize({ ...editSize, isActive: e.target.checked })}
                                    className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                  />
                                  <span className="text-sm text-gray-700">Active</span>
                                </label>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(size.id)}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                  >
                                    ✅ Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                  >
                                    ❌ Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-800">{size.label}</p>
                                  <p className="text-sm text-gray-500">
                                    Order: {size.display_order} • {size.is_active ? '✅ Active' : '❌ Inactive'}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleStartEdit(size)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(size.id)}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSizeManager;
